import React, { useCallback, useEffect, useRef, useState } from "react";
import { getComments, addComment, editComment, deleteComment } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { MessageSquare, Send, Pencil, Trash2, Check, X, AtSign, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const initials = (name) => name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const renderMentions = (text, users) => {
  const names = [...users, ...users.map((u) => u.split(" ")[0])].sort((a, b) => b.length - a.length);
  const rx = new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g");
  return text.split(rx).map((part, i) =>
    part.startsWith("@") ? (
      <span key={`${i}-${part}`} data-testid="mention-chip" className="rounded bg-primary/15 px-1 py-0.5 font-medium text-primary">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${i}-${part}`}>{part}</React.Fragment>
    )
  );
};

const CommentItem = ({ c, me, users, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.text);
  const mine = c.author === me;

  const save = async () => {
    if (!text.trim()) return;
    await onEdit(c.id, text);
    setEditing(false);
  };

  return (
    <div className="flex gap-3" data-testid={`comment-${c.id}`}>
      <Avatar className="h-8 w-8 border border-border shrink-0">
        <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-semibold">{initials(c.author)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium">{c.author}</span>
          <span className="text-muted-foreground font-mono">{new Date(c.created_at).toLocaleString()}</span>
          {c.edited_at && <span className="text-muted-foreground italic">edited</span>}
          {mine && !editing && (
            <span className="ml-auto flex items-center gap-0.5">
              <button data-testid={`comment-edit-${c.id}`} onClick={() => setEditing(true)} className="p-1 text-muted-foreground hover:text-foreground">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c.id)} className="p-1 text-muted-foreground hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
        {editing ? (
          <div className="mt-1.5 space-y-2">
            <Textarea data-testid={`comment-edit-input-${c.id}`} value={text} onChange={(e) => setText(e.target.value)} className="min-h-[60px] text-sm" />
            <div className="flex gap-2">
              <Button data-testid={`comment-save-${c.id}`} size="sm" onClick={save}><Check className="h-3.5 w-3.5 mr-1" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(c.text); }}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm whitespace-pre-wrap break-words" data-testid="comment-text">{renderMentions(c.text, users)}</p>
        )}
      </div>
    </div>
  );
};

export const CommentsThread = ({ invoiceId, onChange }) => {
  const { user, users } = useApp();
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => getComments(invoiceId).then(setComments), [invoiceId]);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await addComment(invoiceId, text.trim(), user);
      setText("");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post comment");
    } finally {
      setBusy(false);
    }
  };

  const onEdit = async (id, t) => {
    try {
      await editComment(id, t.trim(), user);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not edit comment");
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteComment(id, user);
      await load();
      onChange?.();
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete comment");
    }
  };

  const mention = (u) => {
    setText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}@${u} `);
    inputRef.current?.focus();
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="comments-thread">
      <div className="border-b border-border px-5 py-3 text-sm font-medium flex items-center justify-between">
        <span className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> Reviewer Notes
          {comments && <span className="text-xs text-muted-foreground font-mono">({comments.length})</span>}
        </span>
        <span className="text-xs text-muted-foreground">Hand-offs, questions and context for teammates</span>
      </div>

      <div className="p-5 space-y-4">
        {!comments ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="comments-empty">No notes yet. Start the thread below.</p>
        ) : (
          comments.map((c) => <CommentItem key={c.id} c={c} me={user} users={users} onEdit={onEdit} onDelete={onDelete} />)
        )}
      </div>

      <div className="border-t border-border p-4 space-y-2 bg-muted/20">
        <Textarea
          ref={inputRef}
          data-testid="comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
          placeholder={`Leave a note as ${user}… use @Name to mention a teammate`}
          className="min-h-[70px] resize-none text-sm"
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><AtSign className="h-3.5 w-3.5" /> Mention:</span>
          {users.filter((u) => u !== user).map((u) => (
            <button
              key={u}
              data-testid={`mention-${u.split(" ")[0].toLowerCase()}`}
              onClick={() => mention(u)}
              className={cn("rounded-md border border-border px-2 py-0.5 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-colors")}
            >
              @{u}
            </button>
          ))}
          <Button data-testid="comment-submit" size="sm" className="ml-auto" onClick={submit} disabled={busy || !text.trim()}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Post note
          </Button>
        </div>
      </div>
    </div>
  );
};
